import { Outlet, useParams } from "react-router";
import {
  NavigationGroup,
  ProcessPage,
  ProcessPageMain,
  ProcessPageMainContent,
  ProcessPageSidebar,
  ProcessPageSidebarFooter,
  ProcessPageSidebarNavigation,
  ProcessPageSkeleton,
} from "@/shared/bsm/core/process/components/ui/process-page";
import t from "@/lang/lang";
import { useProcessesProcessShowQuery } from "@/services/backend/processes/api/process-api";
import { RTKQueryErrorAlert } from "@/shared/components/errors/rtk-query-error-alert";
import { CloseProcessSidebarMenu } from "@/routes/processes/[processId]/_components/close-process-sidebar-menu";
import {
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
} from "@/shared/components/ui/sidebar";
import { DynamicsSalesLinksSidebarGroup } from "@/routes/processes/[processId]/_components/dynamics-sales-links-sidebar-group";
import {
  Contacts,
  Offers,
  Overview,
} from "@/shared/bsm/core/process/components/ui/process-page-navigation-item";
import { ProcessKind } from "@bitory-gmbh/bsm";
import { useFeatureAuth } from "@/services/backend/auth/hooks/use-feature-auth";
import { Alert403 } from "@/shared/components/alerts/alert-403";
import { MainPageContent } from "@/routes/_components/main-page";
import { useBooleanFlagValue } from "@openfeature/react-sdk";

export default function OmniRoute() {
  const { processId } = useParams();
  const {
    data: process,
    isLoading,
    error,
  } = useProcessesProcessShowQuery({
    id: processId ?? "",
  });

  const sidebarNavigation = useSidebarNavigation(process?.processKind);

  const isReleased = useBooleanFlagValue("gsm.omni", true);
  const canAccess = useFeatureAuth("gsm.omni");
  if (!canAccess || !isReleased) {
    return (
      <MainPageContent>
        <Alert403 />
      </MainPageContent>
    );
  }

  return (
    <ProcessPage>
      {isLoading && <ProcessPageSkeleton />}
      {error && (
        <ProcessPageMain>
          <ProcessPageMainContent>
            <RTKQueryErrorAlert error={error} />
          </ProcessPageMainContent>
        </ProcessPageMain>
      )}
      {process && (
        <>
          <ProcessPageSidebar className="pt-16">
            <ProcessPageSidebarNavigation groups={sidebarNavigation} />
            <ProcessPageSidebarFooter>
              <SidebarGroup>
                <SidebarGroupLabel>{t("Vorgang")}</SidebarGroupLabel>
                <SidebarGroupContent>
                  <CloseProcessSidebarMenu processId={process.id} />
                </SidebarGroupContent>
              </SidebarGroup>
              <DynamicsSalesLinksSidebarGroup processId={process.id} />
            </ProcessPageSidebarFooter>
          </ProcessPageSidebar>
          <Outlet />
        </>
      )}
    </ProcessPage>
  );
}

function useSidebarNavigation(processKind?: ProcessKind): NavigationGroup[] {
  const offersReleased = useBooleanFlagValue("gsm.omni.offers", true);

  if (!processKind) {
    return [];
  }

  // Omni processes have offers (and their contracts) only.
  return [
    {
      label: t("Allgemein"),
      items: [Overview, Contacts],
    },
    {
      label: t("Meilensteine"),
      items: offersReleased ? [Offers] : [],
    },
  ];
}
